import { TIPO_INSPECAO } from "@/constants/TIPO_INSPECAO";
import { useAnaliseLaboratorial } from "@/hooks/useAnaliseLaboratorial";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePermissao } from "@/hooks/usePermissao";
import { IAnaliseLaboratorial } from "@/interfaces/IAnaliseLaboratorial";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MinusOutlined,
  PrinterOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { App, Button, List, Space, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import { ImpressaoAnaliseLaboratorial } from "../../../_components/ImpressaoAnaliseLaboratorial";
import { ModalCancelarAnaliseLaboratorial } from "../../../_components/ModalCancelarAnaliseLaboratorial";

export function ItemListRequisicao({ item }: { item: IAnaliseLaboratorial }) {
  const { isOnline } = useNetworkStatus();
  const requisicaoRef = useRef<HTMLDivElement>(null);
  const { submitRequisicaoApi, updateRequisicao, removeRequisicao } =
    useAnaliseLaboratorial();
  const { hasEnabledPermissao } = usePermissao();
  const { notification, message, modal } = App.useApp();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModalCancelar, setIsOpenModalCancelar] = useState(false);

  const handleSync = async () => {
    try {
      message.loading({
        content: "Sincronizando requisição...",
        key: "loading",
        duration: 0,
      });
      setIsLoading(true);
      const result = await submitRequisicaoApi(item);
      setIsLoading(false);
      message.destroy("loading");

      updateRequisicao({
        ...item,
        id: result.id,
      });

      notification.success({
        message: "Requisição sincronizada com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao sincronizar a requisição:", error);
      message.destroy("loading");
      setIsLoading(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível sincronizar a requisição",
        description: messageError,
      });
    }
  };

  const handleRemove = () => {
    modal.confirm({
      title: "Remover Requisição",
      content: "Tem certeza que deseja remover esta requisição?",
      okText: "Remover",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        removeRequisicao(item.offlineId!);
        notification.success({
          message: "Requisição removida com sucesso!",
        });
      },
    });
  };

  const redirectToEdit = () => {
    navigate(
      `/analises-laboratoriais/requisicoes/lista/editar/${item.offlineId}`
    );
  };

  const redirectToView = () => {
    navigate(`/analises-laboratoriais/visualizar/${item.id}`);
  };

  const handleRemoveLocal = () => {
    removeRequisicao(item.offlineId!);
  };

  const handlePrint = useReactToPrint({
    contentRef: requisicaoRef,
    documentTitle: "requisicao_analise_laboratorial",
    suppressErrors: true,
    preserveAfterPrint: true,
    pageStyle: "@page{size:A4 !important; }",
  });

  const handleOpenModalCancelar = () => {
    setIsOpenModalCancelar(true);
  };

  const produtoDesc = item.produto
    ? `${item.produto?.produto?.nome} ${item.produto?.numeroRotulo}`
    : "Água";

  return (
    <>
      <List.Item
        extra={
          <Space>
            {!item.id && (
              <Button
                type="primary"
                size="small"
                ghost
                icon={<UploadOutlined />}
                onClick={handleSync}
                disabled={!isOnline || isLoading}
                loading={isLoading}
              >
                Sincronizar
              </Button>
            )}

            <Tooltip title="Imprimir" destroyOnHidden>
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              />
            </Tooltip>

            {!item.id && (
              <Tooltip title="Editar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={redirectToEdit}
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Visualizar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={redirectToView}
                  disabled={!isOnline || !hasEnabledPermissao("RAL_VISUALIZAR")}
                />
              </Tooltip>
            )}

            {!item.id && (
              <Tooltip title="Remover" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemove}
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Cancelar" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleOpenModalCancelar}
                  disabled={!isOnline || !hasEnabledPermissao("RAL_CANCELAR")}
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Remover (local)" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<MinusOutlined />}
                  onClick={handleRemoveLocal}
                  disabled={!isOnline}
                />
              </Tooltip>
            )}
          </Space>
        }
      >
        <List.Item.Meta
          title={
            <Typography.Text
              strong
              style={{
                color: isOpenModalCancelar ? "#d7263d" : undefined,
                opacity: item.id ? 1 : 0.6,
              }}
            >
              {item.id
                ? `Requisição ${item.codigo || ""}`
                : "Requisição (Pendente)"}
            </Typography.Text>
          }
          description={
            <Typography.Text
              style={{
                color: isOpenModalCancelar ? "#d7263d" : "#8c8c8c",
                fontSize: 12,
              }}
            >
              Estabelecimento: {item.estabelecimento.numeroInspecao}-
              {TIPO_INSPECAO[item.estabelecimento.tipoInspecao]}-
              {item.estabelecimento.nome}
              <br />
              Produto: {produtoDesc}
              <br />
              Data Coleta: {dayjs(item.dataColeta).format("DD/MM/YYYY HH:mm")}
            </Typography.Text>
          }
        />
      </List.Item>

      <div style={{ display: "none" }}>
        <ImpressaoAnaliseLaboratorial
          marcadagua=""
          ref={requisicaoRef}
          item={item}
        />
      </div>

      <ModalCancelarAnaliseLaboratorial
        requisicaoId={item.id!}
        isOpen={isOpenModalCancelar}
        onClose={() => {
          setIsOpenModalCancelar(false);
        }}
        reloadItems={() => {}}
      />
    </>
  );
}
